import Masonry, { ResponsiveMasonry } from "react-responsive-masonry"
import SinglePost from './SinglePost'
import LightboxModal from './Elements/Modal';
import SwipeView from './Elements/SwipeView';
import useSwipeViewer from '../../../Frontend/Hooks/useSwipeViewer';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const MasonryLayout = ({ feedSettings, posts, headerData, isPro, editorActiveDevice, isFeedEditor }) => {
    const [activePost, setActivePost] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [isLightboxVisible, setIsLightboxVisible] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // SMASH-1377 v3-r11: restore focus to the triggering post button when the
    // conditionally-mounted lightbox closes (no Radix DialogTrigger to restore
    // to, so focus would otherwise drop to <body> — WCAG 2.4.3).
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!isLightboxVisible && triggerRef.current) {
            triggerRef.current.focus();
            triggerRef.current = null;
        }
    }, [isLightboxVisible]);

    let desktopNumber = feedSettings.masonryDesktopColumns;

    if (isFeedEditor) {
        switch (editorActiveDevice.device) {
            case 'mobile':
                desktopNumber = parseInt(feedSettings.masonryMobileColumns);
                break;
            case 'tablet':
                desktopNumber = parseInt(feedSettings.masonryTabletColumns);
                break;
            default:
                desktopNumber = parseInt(feedSettings.masonryDesktopColumns);
                break;
        }
    }

    const masonryBreakPoint = {
        350: parseInt(feedSettings.masonryMobileColumns),
        750: parseInt(feedSettings.masonryTabletColumns),
        900: desktopNumber
    };

    // SMASH-1853: Swipe View (Pro-gated, spec §2.1/§10) intercepts the click
    // when the post is video-eligible; every other post keeps this exact
    // lightbox path unchanged (spec §2.2 — non-eligible activation MUST open
    // the existing lightbox).
    const swipe = useSwipeViewer({ isPro, feedSettings });

    //
    const handleModalOpen = (postIndex, triggerEl) => {
        triggerRef.current = triggerEl || null;
        setActivePost(posts[postIndex]);
        setActiveIndex(postIndex);
        setIsLightboxVisible(true);
    }

    //
    const handlePostActivate = (postIndex, triggerEl) => {
        swipe.handlePostActivate(posts, postIndex, triggerEl, handleModalOpen);
    }

    //
    const handleCloseModal = () => {
        setActivePost(null);
        setIsLightboxVisible(false);
        document.body.classList.remove('sbt-lightbox-modal-open');
    }

    //
    const handlePreviousItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === 0) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex - 1]);
            setActiveIndex(currentPostIndex - 1);
        }
    }

    //
    const handleNextItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === posts.length - 1) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex + 1]);
            setActiveIndex(currentPostIndex + 1);
        }
    }


    return (
        <>
            <ResponsiveMasonry
                columnsCountBreakPoints={masonryBreakPoint}
            >
                <Masonry
                    gutter={`${feedSettings.horizontalSpacing}px`}
                >
                    {
                        posts.map((post, postIndex) =>
                            <SinglePost
                                post={post}
                                headerData={headerData}
                                postIndex={postIndex}
                                feedSettings={feedSettings}
                                key={postIndex}
                                isPro={isPro}
                                isFeedEditor={isFeedEditor}
                                handlePostClick={handlePostActivate}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                activeIndex={activeIndex}
                                setActiveIndex={setActiveIndex}
                            />
                        )
                    }
                </Masonry>
            </ResponsiveMasonry>

            {
                isLightboxVisible && activePost &&
                createPortal(
                    <LightboxModal closeModal={handleCloseModal}
                        post={activePost}
                        headerData={headerData}
                        feedSettings={feedSettings}
                        handleNext={handleNextItem}
                        handlePrev={handlePreviousItem}
                        showNext={
                            !(activeIndex === posts.length - 1)
                        }
                        showPrev={
                            !(activeIndex === 0)
                        }
                        isPro={isPro}
                        isLightboxVisible={isLightboxVisible}
                        setIsLightboxVisible={setIsLightboxVisible}
                    />,
                    document.body
                )
            }

            {swipe.isSwipeOpen &&
                createPortal(
                    <SwipeView
                        posts={swipe.swipeTrack}
                        startIndex={swipe.swipeStartIndex}
                        headerData={headerData}
                        counts={swipe.counts}
                        opener={swipe.opener}
                        onClose={swipe.closeSwipeView}
                    />,
                    document.body
                )
            }
        </>
    )
}

export default MasonryLayout;